import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useApp } from '../store/AppContext'
import type { ID } from '../lib/types'

/** Matches the width set on the panel below; the placing maths needs it. */
const PANEL_W = 240
const GAP = 6
/** Keeps the panel off the very edge of the window. */
const MARGIN = 8

/**
 * Who made this, when, and what it belongs to.
 *
 * The provenance of a project or a client was nowhere on screen. It lived in
 * audit_log, which only an admin can read and no screen renders, so the
 * ordinary question -- "who added this one?" -- had no ordinary answer.
 *
 * A popover rather than a title attribute. A native tooltip cannot be opened
 * from the keyboard, cannot be reached by touch at all, and disappears while
 * you are still reading it; this is three facts somebody wants to sit and
 * look at, not a hint.
 *
 * Deliberately read-only. It is a label that happens to need a click, so it
 * carries no actions and nothing in it can change anything.
 *
 * ---- why this is a portal ----
 *
 * The panel renders into document.body with fixed coordinates rather than
 * absolutely inside the row. Both screens put these rows in `.card--clip`,
 * which is `overflow: hidden` so the header's fill stays inside the rounded
 * corners -- and an absolutely positioned child cannot escape a clipping
 * ancestor. On a full list that went unnoticed, because the panel opened
 * over the rows below it. Filter the list down to one row and the card is
 * barely taller than that row, so the panel was sliced off at the card's
 * edge with only a sliver showing.
 *
 * Fixed coordinates mean the position has to be recomputed while the page
 * moves, which is what the scroll and resize listeners below are for.
 */
export function InfoDot({
  createdBy,
  createdAt,
  clientId,
  what,
  align = 'right',
}: {
  createdBy: ID | null
  createdAt: string | null
  /** Omitted for a client, which does not belong to one. */
  clientId?: ID | null
  /** 'project' or 'client', for the labels and the accessible name. */
  what: string
  align?: 'left' | 'right'
}) {
  const { member, client } = useApp()
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  const anchorRef = useRef<HTMLSpanElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)

  /**
   * Put the panel under the button, and inside the window.
   *
   * Measured rather than assumed: the height depends on how long the names
   * are, and a panel near the bottom of the screen has to flip above its
   * button instead of running off.
   */
  const place = useCallback(() => {
    const anchor = anchorRef.current
    if (!anchor) return
    const r = anchor.getBoundingClientRect()

    const wanted = align === 'right' ? r.right - PANEL_W : r.left
    const left = Math.max(MARGIN, Math.min(wanted, window.innerWidth - PANEL_W - MARGIN))

    const h = panelRef.current?.offsetHeight ?? 0
    const below = r.bottom + GAP
    const top = below + h > window.innerHeight - MARGIN ? Math.max(MARGIN, r.top - GAP - h) : below

    setPos({ top, left })
  }, [align])

  // Before paint, so the panel is never seen at the wrong coordinates.
  useLayoutEffect(() => {
    if (open) place()
    else setPos(null)
  }, [open, place])

  useEffect(() => {
    if (!open) return

    // Capture phase: the row may sit in its own scrolling container, and a
    // scroll there does not bubble to the window.
    const follow = () => place()
    window.addEventListener('scroll', follow, true)
    window.addEventListener('resize', follow)

    /*
     * Dismissal, written here rather than taken from useDismiss. That hook
     * closes on any pointer-down outside ONE element, and the panel is now
     * outside the span it would be given -- so selecting the text inside the
     * panel would dismiss it.
     */
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (anchorRef.current?.contains(t) || panelRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('scroll', follow, true)
      window.removeEventListener('resize', follow)
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, place])

  /*
   * "Unknown" rather than a guess.
   *
   * created_by is NULL for anything written before the column existed whose
   * audit row had no actor -- a CSV import, a backup restore. Naming
   * somebody would attach a person to work they did not do, which is worse
   * than admitting the record does not say.
   */
  const who = createdBy ? (member(createdBy)?.name ?? 'A former member') : 'Unknown'

  const when = createdAt
    ? new Date(createdAt).toLocaleString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'Unknown'

  const owner = clientId === undefined ? null : (client(clientId ?? null)?.name ?? 'No client')

  return (
    <span ref={anchorRef} style={{ display: 'inline-flex', flexShrink: 0 }}>
      <button
        type="button"
        className="ibtn"
        aria-haspopup="dialog"
        aria-expanded={open}
        title={`Who created this ${what}, and when`}
        aria-label={`Details of this ${what}`}
        onClick={() => setOpen((v) => !v)}
      >
        ⓘ
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            className="popover"
            role="dialog"
            aria-label={`About this ${what}`}
            style={{
              position: 'fixed',
              width: PANEL_W,
              padding: 12,
              top: pos?.top ?? 0,
              left: pos?.left ?? 0,
              // Hidden for the one render before it has been measured, so it
              // is never glimpsed in the top-left corner.
              visibility: pos ? 'visible' : 'hidden',
            }}
          >
            <Line label="Created by" value={who} />
            <Line label="Created" value={when} />
            {owner !== null && <Line label="Client" value={owner} />}
          </div>,
          document.body,
        )}
    </span>
  )
}

function Line({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '3px 0' }}>
      <span className="eyebrow" style={{ flexShrink: 0, minWidth: 74 }}>
        {label}
      </span>
      {/* Wraps rather than truncates: a name cut off at the ellipsis is the
          one thing this popover exists to show. */}
      <span style={{ fontSize: 13, minWidth: 0, overflowWrap: 'anywhere' }}>{value}</span>
    </div>
  )
}
